import { Alert, Col, DatePicker, Form, Input, Row, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ResolvedSelect } from "../../../../components/Select/ResolvedSelect.tsx"
import { ProjectTypeSelect } from "../../../../components/Shared/ProjectTypeSelect.tsx"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { withNoneSourceOption } from "../../../Leads/constants/lead-source-none.ts"
import type { ProjectWizardFormData } from "./project-wizard-data.ts"

const DESCRIPTION_MAX_LENGTH = 500

export function ProjectWizardDetailsStep() {
  const { t } = useTranslation()
  const { getDateValueProps } = useDateFormat()

  // The list that mounts this wizard already loaded these for its own
  // filters — no need to fetch them again here.
  const filterOptions = useAppSelector((state) => state.projects.filterOptions)
  const sourceOptions = useMemo(
    () => withNoneSourceOption(filterOptions?.source),
    [filterOptions]
  )

  return (
    <>
      <Typography.Title level={5}>
        {t("projectWizard.project.sectionTitle")}
      </Typography.Title>
      <Typography.Paragraph type={"secondary"}>
        {t("projectWizard.project.introduction")}
      </Typography.Paragraph>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.project.name")}
            name={"lead_name"}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("projectWizard.project.nameRequired")
              }
            ]}
          >
            <Input placeholder={t("projectWizard.project.namePlaceholder")} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.project.type")}
            name={"project_type"}
            rules={[
              {
                required: true,
                message: t("projectWizard.project.typeRequired")
              }
            ]}
          >
            <ProjectTypeSelect
              placeholder={t("projectWizard.project.typePlaceholder")}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.project.startDate")}
            name={"start_date"}
            getValueProps={getDateValueProps}
          >
            <DatePicker className={"w-full"} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.project.endDate")}
            name={"end_date"}
            getValueProps={getDateValueProps}
          >
            <DatePicker className={"w-full"} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.project.source")}
            name={"source"}
          >
            <ResolvedSelect
              allowClear
              showSearch
              optionFilterProp={"label"}
              options={sourceOptions}
              placeholder={t("projectWizard.project.sourcePlaceholder")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectWizardFormData>
            label={t("projectWizard.project.description")}
            name={"comments"}
          >
            <Input.TextArea
              rows={4}
              maxLength={DESCRIPTION_MAX_LENGTH}
              showCount
              placeholder={t("projectWizard.project.descriptionPlaceholder")}
            />
          </Form.Item>
        </Col>
      </Row>

      <Alert
        type={"info"}
        showIcon
        message={t("projectWizard.project.optionalHint")}
        description={t("projectWizard.project.optionalHintDescription")}
      />
    </>
  )
}
